/**
 * Path Manager
 * High-level API for managing route paths with caching
 */

import { FALLBACK_PATHS } from './constants';
import { 
  getCachedPaths, 
  setCachedPaths, 
  getCachedVersion, 
  generateVersion,
} from './pathCache';
import { extractPathsFromFeatures } from './pathExtractor';

/**
 * Updates paths from features tree if they have changed
 * @param {Array} featuresTree - The features tree from the API
 * @returns {Object} Updated paths object
 */
export function updatePaths(featuresTree) {
  const newVersion = generateVersion(featuresTree);
  const cachedVersion = getCachedVersion();
  
  // Check if we need to regenerate
  if (cachedVersion === newVersion) {
    const cached = getCachedPaths();
    if (cached) {
      return cached;
    }
  }
  
  // Extract paths directly from features (not from routes)
  // This preserves child structure even when routes use dynamic segments
  const paths = extractPathsFromFeatures(featuresTree);
  
  setCachedPaths(paths, newVersion);
  return paths;
}

/**
 * Gets paths from cache or returns null
 * @returns {Object|null} Paths object or null if not cached
 */
export function getPaths() {
  return getCachedPaths();
}

/**
 * Gets paths with fallback to minimal structure
 * Returns cached paths or minimal base structure with auth/error routes
 * @returns {Object} Paths object (from cache or minimal structure)
 */
export function getPathsWithFallback() {
  const cached = getCachedPaths();
  
  if (cached) {
    return cached;
  }
  
  // Minimal fallback - only non-feature paths
  return { ...FALLBACK_PATHS };
}

/**
 * Initializes paths from features tree
 * @param {Array} featuresTree - The features tree from the API
 * @returns {Object} Paths object
 */
export function initializePaths(featuresTree) {
  return updatePaths(featuresTree);
}

/**
 * Clears the cached paths (re-exported for convenience)
 */
export { clearPathsCache } from './pathCache';
